/**
 * The editing session, as a React hook.
 *
 * Everything about *what may happen* lives in the core reducer and the
 * controller; this only translates between that and React state. It holds no
 * rules of its own — in particular it never decides that something is safe to
 * save, because that decision belongs to code that has tests.
 *
 * The document model is kept in a ref as well as in state. The controller asks
 * for bytes at the moment it writes them, and a stale closure over React state
 * would auto-save the document as it was two keystrokes ago (§23).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DocumentController } from '@moduviewer/core';
import type {
  ConfirmationPort, DocumentSession, FileStore, NotificationPort, OriginalRef,
} from '@moduviewer/core';
import type { FormatHandler, HandlerContext, ValidationReport } from '@moduviewer/handlers';
import { forgetRecoveryLabel, rememberRecoveryLabel } from './recovery-index.js';
import type { OpenedFile } from '@moduviewer/app';

/** Idle time before an auto-save (§23 "after editing pauses"). */
const IDLE_MS = 2_500;

export interface PendingConfirm {
  readonly kind: 'overwrite' | 'replace' | 'loss';
  readonly title: string;
  readonly body: string;
  readonly items?: readonly string[];
  readonly confirmLabel: string;
  readonly danger?: boolean;
  readonly resolve: (approved: boolean) => void;
}

export interface Toast {
  readonly id: number;
  readonly messageKey: string;
  readonly detail?: string;
  readonly tone: 'info' | 'warn' | 'danger';
}

export interface SessionApi<TModel> {
  readonly session: DocumentSession;
  readonly model: TModel;
  readonly editing: boolean;
  readonly dirty: boolean;
  readonly saving: boolean;
  readonly lastSavedAt: number | null;
  readonly validation: ValidationReport | null;
  readonly confirm: PendingConfirm | null;
  readonly toasts: readonly Toast[];

  beginEdit(): void;
  update(next: TModel): void;
  saveAs(name?: string): void;
  /**
   * Save bytes that are not the document — one entry taken out of an archive.
   *
   * It goes through the same store's Save As, so the destination is still a
   * place the user picked through a dialog rather than one the app chose (§13,
   * §21). It does not touch the session: extracting an entry is not an edit.
   */
  saveExtracted(bytes: Uint8Array, name: string): Promise<void>;
  discard(): void;
  dismissToast(id: number): void;
}

export interface UseSessionOptions<TModel> {
  readonly file: OpenedFile;
  readonly handler: FormatHandler<TModel>;
  readonly store: FileStore;
}

export function useDocumentSession<TModel>(
  { file, handler, store }: UseSessionOptions<TModel>,
): SessionApi<TModel> {
  const [session, setSession] = useState<DocumentSession>(file.session);
  const [model, setModel] = useState<TModel>(file.document.model as TModel);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState<ValidationReport | null>(null);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);

  const modelRef = useRef(model);
  modelRef.current = model;
  const toastId = useRef(0);

  const ctx: HandlerContext = useMemo(() => ({
    assessment: file.assessment,
    limits: file.assessment.limits,
    signal: new AbortController().signal,
  }), [file.assessment]);

  const pushToast = useCallback((messageKey: string, tone: Toast['tone'], detail?: string) => {
    const id = ++toastId.current;
    setToasts((current) => [...current, detail === undefined
      ? { id, messageKey, tone }
      : { id, messageKey, tone, detail }]);
    // Long enough to read a sentence, short enough not to sit on the document.
    setTimeout(() => setToasts((c) => c.filter((t) => t.id !== id)), 6_000);
  }, []);

  const controller = useMemo(() => {
    const confirmations: ConfirmationPort = {
      confirmOverwriteOriginal: (original: OriginalRef) => new Promise<boolean>((resolve) => {
        setConfirm({
          kind: 'overwrite',
          title: '원본 파일을 덮어쓸까요?',
          body: `${original.displayName}의 내용이 바뀝니다. 되돌릴 수 없습니다. 새 파일로 저장하면 원본이 그대로 남습니다.`,
          confirmLabel: '덮어쓰기',
          danger: true,
          resolve: (approved) => { setConfirm(null); resolve(approved); },
        });
      }),
      confirmReplaceExisting: (name: string) => new Promise<boolean>((resolve) => {
        setConfirm({
          kind: 'replace',
          title: '같은 이름의 파일이 이미 있습니다',
          body: `${name}을(를) 대체할까요? 다른 이름으로 저장할 수도 있습니다.`,
          confirmLabel: '대체',
          danger: true,
          resolve: (approved) => { setConfirm(null); resolve(approved); },
        });
      }),
      acknowledgeLoss: (items: readonly string[]) => new Promise<boolean>((resolve) => {
        setConfirm({
          kind: 'loss',
          title: '유지되지 않을 수 있는 기능이 있습니다',
          body: '원본 파일은 변경되지 않습니다. 편집한 내용은 새 파일로 저장됩니다.',
          items,
          confirmLabel: '확인',
          resolve: (approved) => { setConfirm(null); resolve(approved); },
        });
      }),
    };

    const notifications: NotificationPort = {
      notify(messageKey, detail) {
        const tone: Toast['tone'] = messageKey.startsWith('error.') ? 'warn' : 'info';
        pushToast(messageKey, tone, detail);
      },
    };

    return new DocumentController(file.session, {
      store,
      confirm: confirmations,
      notify: notifications,
      // Bytes are produced on demand from the *current* model, never from a
      // snapshot taken when the timer was scheduled.
      bytes: async () => {
        if (!handler.exportBytes) throw new Error('this format cannot be exported');
        const result = await handler.exportBytes(modelRef.current, file.source, ctx);
        // A recovery point is about to be written, so this is the moment its
        // label is worth recording: the bytes are useless to a person who
        // cannot tell which document they belong to (§23).
        rememberRecoveryLabel({
          sessionId: file.session.id,
          displayName: file.request.displayName,
          formatId: file.detection.formatId ?? null,
          sizeBytes: result.bytes.length,
        }, Date.now());
        return result.bytes;
      },
      now: () => Date.now(),
    });
  }, [file, handler, store, ctx, pushToast]);

  const run = useCallback(async (command: Parameters<DocumentController['dispatch']>[0]) => {
    const next = await controller.dispatch(command);
    setSession(next);
    return next;
  }, [controller]);

  /* ---- auto-save (§23) ---- */

  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const scheduleIdleSave = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      void run({ kind: 'idle-timeout', atMs: Date.now() });
    }, IDLE_MS);
  }, [run]);

  useEffect(() => () => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
  }, []);

  /**
   * Write a recovery point now, without waiting for the idle timer.
   *
   * The shell raises this when the app is about to be backgrounded. On a phone
   * that is not a hint: the system may reclaim the process and never run it
   * again, so the idle timer's remaining seconds are seconds that will not
   * happen. §23 requires unsaved work to survive that, and this is the last
   * moment at which it can be made to.
   */
  useEffect(() => {
    const persist = (): void => {
      if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
      void run({ kind: 'idle-timeout', atMs: Date.now() });
    };
    window.addEventListener('modu:persist-now', persist);
    return () => window.removeEventListener('modu:persist-now', persist);
  }, [run]);

  /* §23: the app going to the background, or the tab closing, must flush. */
  useEffect(() => {
    const flush = () => { void run({ kind: 'lifecycle-suspend', atMs: Date.now(), cause: 'background' }); };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flush(); };
    const onHide = () => flush();

    document.addEventListener('visibilitychange', onVisibility);
    // `pagehide` fires where `beforeunload` does not — notably on iOS.
    window.addEventListener('pagehide', onHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onHide);
    };
  }, [run]);

  /* Warn before leaving with unsaved work. */
  useEffect(() => {
    const dirty = session.revision > session.savedRevision;
    if (!dirty) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [session.revision, session.savedRevision]);

  /* ---- actions ---- */

  const beginEdit = useCallback(() => { void run({ kind: 'request-edit' }); }, [run]);

  const update = useCallback((next: TModel) => {
    setModel(next);
    modelRef.current = next;
    void run({ kind: 'edit-applied' });
    scheduleIdleSave();
  }, [run, scheduleIdleSave]);

  const saveAs = useCallback((name?: string) => {
    void (async () => {
      setSaving(true);
      setValidation(null);
      try {
        const suggested = name
          ?? (handler.exportBytes
            ? (await handler.exportBytes(modelRef.current, file.source, ctx)).suggestedName
            : file.request.displayName);
        const next = await run({ kind: 'save-as', suggestedName: suggested, atMs: Date.now() });

        // §13 / §42-27: the save is not finished until the file has been
        // reopened and checked.
        if (next.lastSaved && handler.validateExport) {
          const bytes = await store.readAll(next.lastSaved.handle).catch(async () => {
            const exported = await handler.exportBytes!(modelRef.current, file.source, ctx);
            return exported.bytes;
          });
          const report = await handler.validateExport(bytes, modelRef.current, ctx);
          setValidation(report);
          controller.markValidated(report.ok);
          if (report.ok) forgetRecoveryLabel(file.session.id);
          pushToast(
            report.ok ? 'status.originalUnchanged' : 'error.previewFailed',
            report.ok ? 'info' : 'warn',
            report.ok ? '저장한 파일을 다시 열어 확인했습니다' : report.issues.join('; '),
          );
        }
      } finally {
        setSaving(false);
      }
    })();
  }, [run, handler, file, ctx, store, controller, pushToast]);

  const saveExtracted = useCallback(async (bytes: Uint8Array, name: string) => {
    const outcome = await store.saveAs(bytes, name, session.revision);
    pushToast(
      outcome.ok ? 'status.savedAs' : 'error.saveFailed',
      outcome.ok ? 'info' : 'warn',
      outcome.ok ? name : undefined,
    );
  }, [store, session.revision, pushToast]);

  const discard = useCallback(() => {
    setModel(file.document.model as TModel);
    modelRef.current = file.document.model as TModel;
    // There is nothing left to recover, so the offer must go too. An offer to
    // restore work the user deliberately threw away is worse than none (§23).
    forgetRecoveryLabel(file.session.id);
    void run({ kind: 'discard-edits' });
  }, [run, file]);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  return {
    session,
    model,
    editing: session.phase === 'editing' || session.phase === 'saving' || session.phase === 'save-failed',
    dirty: session.revision > session.savedRevision,
    saving,
    lastSavedAt: session.lastSaved?.savedAtMs ?? null,
    validation,
    confirm,
    toasts,
    beginEdit,
    update,
    saveAs,
    saveExtracted,
    discard,
    dismissToast,
  };
}
